// src/shared/ui/components/chart-row-actions.tsx
// Local stand-in for the Signal DS `ChartRowActions` (to migrate back, PROD-3310). The design's chart tables draw a
// row's actions as the one table icon button (design RowIconButton, ChartRecordTable `Actions`): 32 square, white, a
// hairline, the control radius, a 16 Lucide in secondary ink, Delete red at rest. The DS molecule draws 28 squares with
// no edge, its tones by utility classes, and takes no tone or size, so it cannot draw that. This keeps the DS prop
// surface one for one (same handlers, same labels, same "coming soon" state for a missing handler), so a call site
// swaps the import and nothing else, and swaps back once the DS draws the design.
import { CircleCheck, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { RowIconButton, type RowIconTone } from './row-icon-button';

export interface ChartRowIconActionsProps {
  onEdit?: () => void;
  onDelete?: () => void;
  onResolve?: () => void;
  onUnresolve?: () => void;
  editLabel?: string;
  deleteLabel?: string;
  resolveLabel?: string;
  unresolveLabel?: string;
  hideDelete?: boolean;
  className?: string;
}

function Action({ label, onClick, tone, children }: { label: string; onClick?: () => void; tone: RowIconTone; children: ReactNode }) {
  const enabled = typeof onClick === 'function';
  return (
    <RowIconButton
      label={enabled ? label : `${label} (coming soon)`}
      tooltip={enabled ? label : 'Coming in a follow-up PR'}
      tone={tone}
      data-tone={tone}
      disabled={!enabled}
      aria-disabled={!enabled}
      onClick={onClick}
    >
      {children}
    </RowIconButton>
  );
}

export function ChartRowIconActions({
  onEdit,
  onDelete,
  onResolve,
  onUnresolve,
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  resolveLabel = 'Resolve',
  unresolveLabel = 'Unresolve',
  hideDelete = false,
  className,
}: ChartRowIconActionsProps) {
  return (
    <div data-slot="chart-row-actions" className={cn('inline-flex items-center gap-2', className)}>
      {onResolve ? (
        <Action label={resolveLabel} onClick={onResolve} tone="success">
          <CircleCheck className="size-4" aria-hidden="true" />
        </Action>
      ) : null}
      {onUnresolve ? (
        <Action label={unresolveLabel} onClick={onUnresolve} tone="default">
          <RotateCcw className="size-4" aria-hidden="true" />
        </Action>
      ) : null}
      <Action label={editLabel} onClick={onEdit} tone="default">
        <Pencil className="size-4" aria-hidden="true" />
      </Action>
      {hideDelete ? null : (
        <Action label={deleteLabel} onClick={onDelete} tone="destructive">
          <Trash2 className="size-4" aria-hidden="true" />
        </Action>
      )}
    </div>
  );
}
